import type {
  CourseDetail,
  CourseIndexOverrides,
  CourseSearchRecord,
  CourseSummary,
  DepartmentRecord,
  HomeSummary,
  MedianBoardData,
  MedianCourseRecord,
} from "./types";
import { termSortValue } from "./terms";

const GRADED_MEDIAN_VALUES = ["A", "A-", "B+", "B", "B-", "C+"] as const;

function normalizeQuarter(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

function medianScore(value: string | null | undefined): number | null {
  const scores = (value ?? "")
    .split("/")
    .map((part) => GRADED_MEDIAN_VALUES.indexOf(part.trim() as (typeof GRADED_MEDIAN_VALUES)[number]))
    .filter((score) => score >= 0);

  return scores.length > 0
    ? scores.reduce((total, score) => total + score, 0) / scores.length
    : null;
}

function formatOfferingPreview(course: Pick<CourseSummary, "officialOfferings" | "offeredLabel" | "offeredPeriods">): string | null {
  const offering = [...course.officialOfferings]
    .sort((left, right) => termSortValue(right.term) - termSortValue(left.term))[0];

  if (offering) {
    const periods = offering.periods.length > 0 ? `${offering.periods.join("/")} - ` : "";
    return `Offered ${periods}${offering.term}`;
  }

  if (course.offeredLabel) {
    return course.offeredLabel;
  }

  return course.offeredPeriods.length > 0 ? `Offered ${course.offeredPeriods.join(", ")}` : null;
}

export function toCourseSearchRecord(course: CourseSummary): CourseSearchRecord {
  return {
    courseId: course.courseId,
    slug: course.slug,
    shortName: course.shortName,
    title: course.title,
    department: course.department,
    departmentName: course.departmentName,
    distribs: course.distribs,
    offeringPreview: formatOfferingPreview(course),
    legacyReviewCount: course.legacyReviewCount,
    professorNames: course.professorNames,
  };
}

export function toMedianCourseRecord(course: CourseSummary, currentQuarter: string): MedianCourseRecord | null {
  if (!course.officialOfferings.some((offering) => normalizeQuarter(offering.term) === currentQuarter)) {
    return null;
  }

  return {
    ...toCourseSearchRecord(course),
    typicalMedian: course.typicalMedian ?? null,
    typicalMedianReviewCount: course.typicalMedianReviewCount ?? 0,
  };
}

function compareMedianCourses(left: MedianCourseRecord, right: MedianCourseRecord): number {
  const leftScore = medianScore(left.typicalMedian) ?? Number.POSITIVE_INFINITY;
  const rightScore = medianScore(right.typicalMedian) ?? Number.POSITIVE_INFINITY;
  if (leftScore !== rightScore) {
    return leftScore - rightScore;
  }
  if (right.typicalMedianReviewCount !== left.typicalMedianReviewCount) {
    return right.typicalMedianReviewCount - left.typicalMedianReviewCount;
  }
  if (right.legacyReviewCount !== left.legacyReviewCount) {
    return right.legacyReviewCount - left.legacyReviewCount;
  }
  return left.shortName.localeCompare(right.shortName);
}

export function buildHomeSummary(
  courses: CourseDetail[] | CourseSummary[],
  departments: DepartmentRecord[],
  generatedAt: string,
): HomeSummary {
  const records = courses.map(toCourseSearchRecord);
  return {
    generatedAt,
    departments: departments.slice(0, 5),
    spotlight: records
      .filter((course) => course.legacyReviewCount > 0)
      .sort((left, right) => {
        if (right.legacyReviewCount !== left.legacyReviewCount) {
          return right.legacyReviewCount - left.legacyReviewCount;
        }
        return left.shortName.localeCompare(right.shortName);
      })
      .slice(0, 5),
  };
}

export function buildMedianBoardData(
  courses: CourseDetail[] | CourseSummary[],
  generatedAt: string,
): MedianBoardData {
  const currentQuarter = courses
    .flatMap((course) => course.officialOfferings.map((offering) => normalizeQuarter(offering.term)))
    .filter(Boolean)
    .sort((left, right) => termSortValue(right) - termSortValue(left))[0] ?? null;

  if (!currentQuarter) {
    return {
      generatedAt,
      currentQuarter: null,
      coursesWithMedian: 0,
      coursesWithoutMedian: 0,
      rankedCourses: [],
    };
  }

  const currentQuarterCourses = courses
    .map((course) => toMedianCourseRecord(course, currentQuarter))
    .filter((course): course is MedianCourseRecord => course !== null);
  const rankedWithMedian = currentQuarterCourses
    .filter((course) => medianScore(course.typicalMedian) !== null)
    .sort(compareMedianCourses);
  const withoutMedian = currentQuarterCourses
    .filter((course) => medianScore(course.typicalMedian) === null)
    .sort((left, right) => {
      if (right.legacyReviewCount !== left.legacyReviewCount) {
        return right.legacyReviewCount - left.legacyReviewCount;
      }
      return left.shortName.localeCompare(right.shortName);
    });

  return {
    generatedAt,
    currentQuarter,
    coursesWithMedian: rankedWithMedian.length,
    coursesWithoutMedian: withoutMedian.length,
    rankedCourses: [...rankedWithMedian, ...withoutMedian],
  };
}

export function buildCourseSearchIndex(courses: CourseDetail[] | CourseSummary[]): CourseSearchRecord[] {
  return courses.map(toCourseSearchRecord);
}

export function mergeHomeSummary(
  base: HomeSummary,
  overrides: CourseIndexOverrides,
  generatedAt = base.generatedAt,
): HomeSummary {
  const candidates = new Map(base.spotlight.map((course) => [course.courseId, course]));
  for (const course of Object.values(overrides)) {
    candidates.set(course.courseId, toCourseSearchRecord(course));
  }

  return {
    ...base,
    generatedAt,
    spotlight: [...candidates.values()]
      .filter((course) => course.legacyReviewCount > 0)
      .sort((left, right) => {
        if (right.legacyReviewCount !== left.legacyReviewCount) {
          return right.legacyReviewCount - left.legacyReviewCount;
        }
        return left.shortName.localeCompare(right.shortName);
      })
      .slice(0, 5),
  };
}

export function mergeMedianBoardData(
  base: MedianBoardData,
  overrides: CourseIndexOverrides,
  generatedAt = base.generatedAt,
): MedianBoardData {
  if (!base.currentQuarter) {
    return { ...base, generatedAt };
  }

  const courses = new Map(base.rankedCourses.map((course) => [course.courseId, course]));
  for (const course of Object.values(overrides)) {
    const nextCourse = toMedianCourseRecord(course, base.currentQuarter);
    if (nextCourse) {
      courses.set(course.courseId, nextCourse);
    } else {
      courses.delete(course.courseId);
    }
  }

  const rankedCourses = [...courses.values()];
  const rankedWithMedian = rankedCourses
    .filter((course) => medianScore(course.typicalMedian) !== null)
    .sort(compareMedianCourses);
  const withoutMedian = rankedCourses
    .filter((course) => medianScore(course.typicalMedian) === null)
    .sort((left, right) => {
      if (right.legacyReviewCount !== left.legacyReviewCount) {
        return right.legacyReviewCount - left.legacyReviewCount;
      }
      return left.shortName.localeCompare(right.shortName);
    });

  return {
    ...base,
    generatedAt,
    coursesWithMedian: rankedWithMedian.length,
    coursesWithoutMedian: withoutMedian.length,
    rankedCourses: [...rankedWithMedian, ...withoutMedian],
  };
}
